import React, { useState } from 'react';
import { Alert, Button, Drawer, Modal, Popconfirm, Select, Space, Spin, Typography, message } from 'antd';
import { DeleteOutlined, ReloadOutlined } from '@ant-design/icons';
import { apiConfig } from '../../authConfig';
import { Role } from '../../constants/roles';
import { PLATFORM_FEED_PROVIDERS } from '../../constants/integrationProviders';
import { useApiToken } from '../../hooks/useApiToken';
import { useResource } from '../../hooks/useResource';
import { useSupportApi } from '../../hooks/useSupportApi';
import {
    deleteAdvisorIntegration, deleteMyCredential, deletePlatformCredential, listAdvisorIntegrations,
    listLeadAdvisorRoster, listMyCredentials, listPlatformCredentials, listProviders, saveAdvisorIntegration,
    saveMyCredential, savePlatformCredential, verifyMyCredential,
} from '../../utils/integrationsApi';
import { statusFromCredential } from './IntegrationCard';
import IntegrationsGrid from './IntegrationsGrid';
import ProviderForm from './ProviderForm';
import PathkeyPanel from './PathkeyPanel';
import PlatformKeyPanel, { platformKeyStatus } from './PlatformKeyPanel';
import { displayEmail } from '../../utils/displayEmail';

const { Text, Paragraph } = Typography;

/** Pill label per provider `kind` the server reports; an unknown kind shows its own name rather than being hidden. */
const KIND_CATEGORY = { ESIGN: 'E-signature', COMPANY_ENRICHMENT: 'Company data' };
const categoryOf = (kind) => KIND_CATEGORY[kind] || kind;
const CATEGORY_PATHKEY = 'Regulatory feed';
const CATEGORY_PLATFORM = 'Platform feeds';

/** loading (no data yet) | error | ready. Reloads keep the previous data, so a save never blanks the grid. */
const phaseOf = (res) => (res.status === 'error' ? 'error' : res.data ? 'ready' : 'loading');

const Loading = () => <div style={{ textAlign: 'center', padding: 32 }}><Spin /></div>;

const ErrorBlock = ({ title, res }) => (
    <Alert
        type="error"
        showIcon
        style={{ marginBottom: 16 }}
        message={title}
        description={res.error?.message || 'Request failed.'}
        action={<Button size="small" icon={<ReloadOutlined />} onClick={res.reload}>Retry</Button>}
    />
);

/** Confirmation for the kebab "Disconnect": `pending = { title, description, run }`. */
const DisconnectModal = ({ pending, onClose }) => {
    const [busy, setBusy] = useState(false);
    const go = async () => {
        setBusy(true);
        try {
            await pending.run();
        } catch (err) {
            message.error(err.message || 'Could not disconnect.');
        } finally {
            setBusy(false);
            onClose();
        }
    };
    return (
        <Modal
            open={!!pending}
            title={pending?.title}
            onCancel={onClose}
            footer={<Space><Button onClick={onClose}>Cancel</Button><Button danger type="primary" loading={busy} onClick={go}>Disconnect</Button></Space>}
        >
            <Paragraph>{pending?.description}</Paragraph>
        </Modal>
    );
};

const normalizeOwn = (c) => c && {
    id: c.id, label: c.displayLabel, status: c.status, updatedAt: c.lastRotatedAt || c.updatedAt, lastVerifiedAt: c.lastVerifiedAt ?? null,
};
const normalizeBehalf = (c) => c && { label: c.label, status: c.status, maskedPreview: c.maskedPreview, updatedAt: c.updatedAt };

/** Provider card from the server's spec + the matching stored credential (own or on-behalf shape). */
const providerCard = (spec, cred, { onOpen, onDisconnect, keyPrefix = '' }) => ({
    key: `${keyPrefix}${spec.providerId}`,
    category: categoryOf(spec.kind),
    name: spec.displayName,
    purpose: spec.description,
    logoKey: spec.providerId,
    status: statusFromCredential(cred),
    updatedAt: cred?.updatedAt,
    menu: cred ? [{ key: 'disconnect', label: 'Disconnect', danger: true, onSelect: onDisconnect }] : [],
    onOpen,
});

/** Lead Advisor: every provider the server lists + the Pathkey card (when /me allows it). */
const LeadAdvisorConnections = () => {
    const getToken = useApiToken();
    const api = useSupportApi();
    const providers = useResource(() => listProviders(getToken), [getToken]);
    const creds = useResource(() => listMyCredentials(getToken), [getToken]);
    const me = useResource(() => api.me(), [api]);
    const pathkeyAllowed = me.status === 'ready' && !!me.data?.canConfigurePathkey;
    const pathkey = useResource(() => api.getPathkeyConfig(), [api], { enabled: pathkeyAllowed });
    const [open, setOpen] = useState(null); // 'pathkey' | providerId | null
    const [pending, setPending] = useState(null);

    const reloadCreds = () => creds.reload();
    const specs = providers.data || [];
    const stored = creds.data || [];
    const known = new Set(specs.map((p) => p.providerId));
    const legacy = stored.filter((c) => !known.has(c.provider));
    const credOf = (id) => stored.find((c) => c.provider === id) || null;
    const openSpec = specs.find((p) => p.providerId === open);

    const removeLegacy = async (c) => {
        try {
            await deleteMyCredential(getToken, c.id);
            message.success('Credentials removed.');
        } catch (err) {
            message.error(err.message || 'Could not remove credentials.');
        }
        await reloadCreds();
    };

    const providersPhase = phaseOf(providers);
    const credsPhase = phaseOf(creds);
    const ready = providersPhase === 'ready' && credsPhase === 'ready';
    const pathkeyCfg = pathkey.data || null;

    const cards = [];
    if (ready) {
        for (const spec of specs) {
            const cred = normalizeOwn(credOf(spec.providerId));
            cards.push(providerCard(spec, cred, {
                onOpen: () => setOpen(spec.providerId),
                onDisconnect: () => setPending({
                    title: `Disconnect ${spec.displayName}?`,
                    description: 'The stored secrets are deleted and cannot be recovered.',
                    run: async () => {
                        await deleteMyCredential(getToken, cred.id);
                        message.success(`${spec.displayName} disconnected.`);
                        await reloadCreds();
                    },
                }),
            }));
        }
    }
    if (pathkeyAllowed && pathkey.status === 'ready') {
        // Pathkey has no delete endpoint (only an Active toggle), so its kebab offers Configure only.
        cards.push({
            key: 'pathkey',
            category: CATEGORY_PATHKEY,
            name: 'Pathkey',
            purpose: 'Statutory and regulatory feed delivered to your firm by signed webhook.',
            logoKey: 'PATHKEY',
            status: pathkeyCfg ? (pathkeyCfg.isActive ? 'connected' : 'attention') : 'none',
            updatedAt: pathkeyCfg?.updatedAt,
            menu: [],
            onOpen: () => setOpen('pathkey'),
        });
    }

    const loading = providersPhase === 'loading' || (providersPhase === 'ready' && credsPhase === 'loading')
        || (pathkeyAllowed && pathkey.status === 'loading' && !pathkey.data);

    return (
        <div>
            {providersPhase === 'error' && <ErrorBlock title="Could not load the integrations catalogue" res={providers} />}
            {credsPhase === 'error' && <ErrorBlock title="Could not load your stored credentials" res={creds} />}
            {me.status === 'error' && (
                <Alert type="warning" showIcon style={{ marginBottom: 16 }} message="Could not determine Pathkey access" description={me.error.message}
                    action={<Button size="small" onClick={me.reload}>Retry</Button>} />
            )}
            {pathkeyAllowed && pathkey.status === 'error' && <ErrorBlock title="Could not load the Pathkey configuration" res={pathkey} />}
            {loading && <Loading />}
            {!loading && ready && cards.length === 0 && providersPhase === 'ready' && (
                <Alert type="info" showIcon message="No integrations are available on this server." />
            )}
            {!loading && cards.length > 0 && <IntegrationsGrid cards={cards} />}

            {legacy.length > 0 && (
                <div className="int-panel" style={{ marginTop: 24 }}>
                    <div className="int-section-title" style={{ marginTop: 0 }}>Unsupported credentials</div>
                    <Paragraph type="secondary">
                        These stored credentials belong to providers that are not supported. They are not used by anything and can be removed.
                    </Paragraph>
                    {legacy.map((c) => (
                        <div key={c.id} style={{ marginBottom: 8 }}>
                            <Text>{c.provider}{c.displayLabel ? ` (${c.displayLabel})` : ''}</Text>{' '}
                            <Popconfirm title="Remove these credentials?" okText="Remove" okButtonProps={{ danger: true }} onConfirm={() => removeLegacy(c)}>
                                <Button size="small" danger icon={<DeleteOutlined />}>Remove</Button>
                            </Popconfirm>
                        </div>
                    ))}
                </div>
            )}

            <DisconnectModal pending={pending} onClose={() => setPending(null)} />

            <Drawer
                open={!!openSpec || open === 'pathkey'}
                onClose={() => setOpen(null)}
                width={520}
                title={open === 'pathkey' ? 'Pathkey' : openSpec?.displayName}
                destroyOnClose
            >
                {openSpec && (() => {
                    const cred = credOf(openSpec.providerId);
                    const webhookUrl = openSpec.webhook && cred
                        ? `${apiConfig.baseUrl}${openSpec.webhook.pathTemplate.replace('{credentialId}', cred.id)}`
                        : null;
                    return (
                        <>
                            <Paragraph type="secondary">{openSpec.description}</Paragraph>
                            <ProviderForm
                                spec={openSpec}
                                existing={normalizeOwn(cred)}
                                webhookUrl={webhookUrl}
                                webhookNote={openSpec.webhook?.note}
                                onSave={async ({ secrets, label }) => {
                                    const data = await saveMyCredential(getToken, { provider: openSpec.providerId, secrets, displayLabel: label });
                                    message.success(data?.reconnected ? `${openSpec.displayName} credentials replaced.` : `${openSpec.displayName} credentials saved.`);
                                    await reloadCreds();
                                }}
                                onRemove={async () => {
                                    await deleteMyCredential(getToken, cred.id);
                                    message.success(`${openSpec.displayName} disconnected.`);
                                    await reloadCreds();
                                }}
                                onVerify={async () => {
                                    try {
                                        await verifyMyCredential(getToken, cred.id);
                                        message.success(`${openSpec.displayName} credentials verified with the provider.`);
                                    } finally {
                                        await reloadCreds();
                                    }
                                }}
                            />
                        </>
                    );
                })()}
                {open === 'pathkey' && pathkeyAllowed && (
                    <PathkeyPanel api={api} current={pathkeyCfg} onChanged={pathkey.reload} />
                )}
            </Drawer>
        </div>
    );
};

/** Platform admin: platform feed keys + every Lead-Advisor-level card for the advisor chosen in the bar above the grid. */
const AdminConnections = () => {
    const getToken = useApiToken();
    const platform = useResource(() => listPlatformCredentials(getToken), [getToken]);
    const roster = useResource(() => listLeadAdvisorRoster(getToken), [getToken]);
    const [advisorId, setAdvisorId] = useState(undefined);
    const hasAdvisor = !!advisorId;
    const providers = useResource(() => listProviders(getToken), [getToken, advisorId], { enabled: hasAdvisor });
    // The loader tags its rows with the advisor they belong to, so a late or stale response is never shown for another advisor.
    const ints = useResource(
        () => listAdvisorIntegrations(getToken, advisorId).then((rows) => ({ advisorId, rows })),
        [getToken, advisorId],
        { enabled: hasAdvisor },
    );
    const [open, setOpen] = useState(null); // { type: 'platform' | 'advisor', id }
    const [pending, setPending] = useState(null);

    const advisors = roster.data || [];
    const advisor = advisors.find((a) => a.id === advisorId) || null;
    const who = advisor ? (advisor.name || displayEmail(advisor.email)) : '';
    const platformRows = platform.data || [];
    const specs = providers.data || [];
    const intRows = ints.data && ints.data.advisorId === advisorId ? ints.data.rows : null;
    const rowOf = (id) => (intRows || []).find((r) => r.provider === id) || null;

    const providerOf = (c) => PLATFORM_FEED_PROVIDERS.find((p) => p.id === c.provider) || { id: c.provider, label: c.provider, description: '' };
    const openCred = open?.type === 'platform' ? platformRows.find((c) => c.provider === open.id) : null;
    const openSpec = open?.type === 'advisor' && advisor ? specs.find((p) => p.providerId === open.id) : null;

    const cards = platformRows.map((c) => ({
        key: `platform:${c.provider}`,
        category: CATEGORY_PLATFORM,
        name: providerOf(c).label,
        purpose: providerOf(c).description || 'Platform-wide feed key.',
        logoKey: c.provider,
        status: platformKeyStatus(c),
        updatedAt: c.id ? c.lastRotatedAt || c.updatedAt : null,
        note: c.source === 'env' ? 'Environment key in use' : undefined,
        menu: c.id ? [{
            key: 'disconnect',
            label: 'Revoke key',
            danger: true,
            onSelect: () => setPending({
                title: `Revoke ${providerOf(c).label}?`,
                description: 'The feed will stop authenticating until a new key is entered.',
                run: async () => {
                    await deletePlatformCredential(getToken, c.provider);
                    message.success(`${providerOf(c).label} revoked.`);
                    await platform.reload();
                },
            }),
        }] : [],
        onOpen: () => setOpen({ type: 'platform', id: c.provider }),
    }));
    if (hasAdvisor && providers.status === 'ready' && intRows) {
        for (const spec of specs) {
            const row = normalizeBehalf(rowOf(spec.providerId));
            cards.push(providerCard(spec, row, {
                keyPrefix: 'advisor:',
                onOpen: () => setOpen({ type: 'advisor', id: spec.providerId }),
                onDisconnect: () => setPending({
                    title: `Disconnect ${spec.displayName} for ${who}?`,
                    description: `${who} will no longer see this integration as connected. The stored secrets are deleted.`,
                    run: async () => {
                        await deleteAdvisorIntegration(getToken, advisorId, spec.providerId);
                        message.success(`${spec.displayName} disconnected for ${who}.`);
                        await ints.reload();
                    },
                }),
            }));
        }
    }

    const advisorLoading = hasAdvisor && (providers.status === 'loading' || providers.status === 'idle' || ints.status === 'loading' || (ints.status === 'ready' && !intRows)) && providers.status !== 'error' && ints.status !== 'error';

    return (
        <div>
            <div className="int-bar">
                <div>
                    <div className="int-bar-label">Configuring for:</div>
                    <div className="int-bar-hint">
                        {advisor
                            ? `Changes below are made on behalf of ${who}. They will see these integrations as connected; secrets are never shown back.`
                            : 'Pick a Lead Advisor to configure their integrations. Platform feeds below apply to all firms.'}
                    </div>
                </div>
                {phaseOf(roster) === 'loading' && <Spin size="small" />}
                {phaseOf(roster) === 'ready' && advisors.length > 0 && (
                    <Select
                        aria-label="Lead Advisor"
                        className="int-bar-select"
                        showSearch
                        optionFilterProp="label"
                        placeholder="Choose a Lead Advisor"
                        value={advisorId}
                        onChange={setAdvisorId}
                        options={advisors.map((a) => ({ value: a.id, label: a.name ? `${a.name} (${displayEmail(a.email)})` : displayEmail(a.email) }))}
                    />
                )}
                {phaseOf(roster) === 'ready' && advisors.length === 0 && <Text type="secondary">There are no Lead Advisors to configure yet.</Text>}
            </div>

            {roster.status === 'error' && <ErrorBlock title="Could not load the Lead Advisor list" res={roster} />}
            {platform.status === 'error' && <ErrorBlock title="Could not load platform keys" res={platform} />}
            {hasAdvisor && providers.status === 'error' && <ErrorBlock title="Could not load the integrations catalogue" res={providers} />}
            {hasAdvisor && ints.status === 'error' && <ErrorBlock title={`Could not load ${who}'s integrations`} res={ints} />}
            {(phaseOf(platform) === 'loading' || advisorLoading) && <Loading />}
            {phaseOf(platform) === 'ready' && platformRows.length === 0 && !hasAdvisor && (
                <Alert type="info" showIcon style={{ marginBottom: 16 }} message="The server reports no platform providers." />
            )}
            {cards.length > 0 && <IntegrationsGrid cards={cards} />}

            <DisconnectModal pending={pending} onClose={() => setPending(null)} />

            <Drawer open={!!openCred} onClose={() => setOpen(null)} width={520} title={openCred ? providerOf(openCred).label : ''} destroyOnClose>
                {openCred && (
                    <PlatformKeyPanel
                        provider={providerOf(openCred)}
                        credential={openCred}
                        onSave={async ({ secretValue, displayLabel }) => {
                            const data = await savePlatformCredential(getToken, { provider: openCred.provider, secretValue, displayLabel });
                            message.success(data?.rotated ? `${providerOf(openCred).label} rotated.` : `${providerOf(openCred).label} saved.`);
                            await platform.reload();
                        }}
                        onRemove={async () => {
                            await deletePlatformCredential(getToken, openCred.provider);
                            message.success(`${providerOf(openCred).label} disconnected.`);
                            await platform.reload();
                        }}
                    />
                )}
            </Drawer>

            <Drawer open={!!openSpec} onClose={() => setOpen(null)} width={520} title={openSpec ? `${openSpec.displayName} for ${who}` : ''} destroyOnClose>
                {openSpec && (
                    <>
                        <Alert
                            type="warning"
                            showIcon
                            style={{ marginBottom: 16 }}
                            message={`You are configuring this on behalf of ${who}${advisor.email && advisor.name ? ` (${displayEmail(advisor.email)})` : ''}`}
                            description={`${who} will see ${openSpec.displayName} as connected in their own Integrations page. Secrets you enter are stored encrypted and are never shown back to anyone.`}
                        />
                        <Paragraph type="secondary">{openSpec.description}</Paragraph>
                        <ProviderForm
                            spec={openSpec}
                            existing={normalizeBehalf(rowOf(openSpec.providerId))}
                            onSave={async ({ secrets, label }) => {
                                await saveAdvisorIntegration(getToken, advisorId, openSpec.providerId, { secrets, label });
                                message.success(`${openSpec.displayName} saved for ${who}.`);
                                await ints.reload();
                            }}
                            onRemove={async () => {
                                await deleteAdvisorIntegration(getToken, advisorId, openSpec.providerId);
                                message.success(`${openSpec.displayName} disconnected for ${who}.`);
                                await ints.reload();
                            }}
                        />
                    </>
                )}
            </Drawer>
        </div>
    );
};

const ConnectionsTab = ({ role }) => (role === Role.ADMIN ? <AdminConnections /> : <LeadAdvisorConnections />);

export default ConnectionsTab;
